import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { FiMail, FiLock, FiBookOpen } from 'react-icons/fi';

export default function LoginPageTeacher() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // Admin'den gelen token'ı kontrol et ve otomatik giriş yap
  useEffect(() => {
    const checkAdminLogin = () => {
      // localStorage'dan token'ı kontrol et
      const token = localStorage.getItem('teacherToken');
      const teacherInfo = localStorage.getItem('teacherInfo');
      
      if (token && teacherInfo) {
        // Token zaten var, sayfayı yenile ki AuthProvider token'ı algılasın
        window.location.href = '/teacher';
      }
    };
    
    checkAdminLogin();
  }, []);

  // Eğer zaten giriş yapmışsa ana sayfaya yönlendir
  if (isAuthenticated) {
    return <Navigate to="/teacher" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        navigate('/teacher');
      } else {
        setError(result.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
      }
    } catch (err) {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Kullanıcı yazmaya devam ederse error'u temizle
    if (error) setError('');
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{background: 'linear-gradient(135deg, #A7AAE1 0%, #9B9CE8 35%, #8A8BED 70%, #7B7CF2 100%)'}}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-purple-300/20 rounded-full mix-blend-multiply filter blur-xl animate-bounce"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-indigo-300/20 rounded-full mix-blend-multiply filter blur-xl animate-bounce delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-blue-300/20 rounded-full mix-blend-multiply filter blur-xl animate-bounce delay-2000"></div>
        <div className="absolute top-3/4 right-1/3 w-72 h-72 bg-violet-300/15 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-8 items-center">

          {/* Left Side - Welcome Section */}
          <div className="text-white space-y-8 lg:pr-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="text-6xl">👨‍🏫</div>
                <div>
                  <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-purple-200 to-indigo-200 bg-clip-text text-transparent">
                    Öğretmen Hub
                  </h1>
                  <p className="text-purple-100 text-xl">Eğitimin kalbi burada atıyor</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-indigo-500 rounded-full flex items-center justify-center text-2xl">
                  ✏️
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Akıllı Ders Planı</h3>
                  <p className="text-purple-100">AI destekli müfredat</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-2xl">
                  📊
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Öğrenci Analizi</h3>
                  <p className="text-purple-100">Detaylı performans takibi</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-400 to-blue-500 rounded-full flex items-center justify-center text-2xl">
                  🎯
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Hedefe Odaklı</h3>
                  <p className="text-purple-100">Sınav başarı stratejileri</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-2xl">
                  💬
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Canlı İletişim</h3>
                  <p className="text-purple-100">Öğrenci-veli konsültasyonu</p>
                </div>
              </div>
            </div>

            <div className="bg-white/15 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-purple-200">2.5K+</div>
                  <div className="text-purple-100 text-sm">Aktif Öğretmen</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-indigo-200">%98</div>
                  <div className="text-purple-100 text-sm">Memnuniyet</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-200">1M+</div>
                  <div className="text-purple-100 text-sm">Verilen Ders</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-cyan-200">∞</div>
                  <div className="text-purple-100 text-sm">İlham</div>
                </div>
              </div>
            </div>

          </div>

          {/* Right Side - Login Form */}
          <div className="relative">
            <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="text-center mb-8">
                <div className="text-4xl mb-4">📚</div>
                <h2 className="text-3xl font-bold text-white mb-2">Hoş Geldiniz</h2>
                <p className="text-purple-100">Eğitim yolculuğunuzu sürdürün</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-500/20 backdrop-blur border border-red-400/50 text-red-100 px-4 py-3 rounded-xl">
                    <span className="block sm:inline">{error}</span>
                  </div>
                )}

                {/* Email Input */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    ✉️ E-posta Adresi
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-white/10 backdrop-blur border border-white/30 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all"
                      placeholder="ogretmen@email.com"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <div className="text-white/40">🧡</div>
                    </div>
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    🗝️ Şifre
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-white/10 backdrop-blur border border-white/30 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all"
                      placeholder="••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <div className="text-white/40">🔥</div>
                    </div>
                  </div>
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center text-white/80">
                    <input
                      type="checkbox"
                      className="mr-2 rounded bg-white/10 border-white/30 text-purple-400 focus:ring-purple-400"
                    />
                    Beni hatırla
                  </label>
                  <a href="#" className="text-purple-300 hover:text-purple-200 font-medium">
                    Şifremi unuttum?
                  </a>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-4 rounded-xl text-white font-bold text-lg transition-all transform hover:scale-105 ${
                    loading
                      ? 'bg-gray-500/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-400 via-indigo-500 to-blue-600 hover:from-purple-500 hover:via-indigo-600 hover:to-blue-700 shadow-lg'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                      Giriş yapılıyor...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="mr-2">🚀</span>
                      Öğretmen Paneline Gir
                    </div>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-white/60 text-sm">
                  💎 Eğitimde Fark Yaratın
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}